/**
 * 자체 오버롤 레이팅 계산 시스템
 * 구글 점수를 배제하고 실제 성능 지표만을 사용한 상대평가 시스템
 */

export interface PerformanceMetrics {
  fcp_desktop?: number;
  fcp_mobile?: number;
  lcp_desktop?: number;
  lcp_mobile?: number;
  tbt_desktop?: number;
  tbt_mobile?: number;
  total_size_desktop?: string | number;
  total_size_mobile?: string | number;
}

export interface SiteData extends PerformanceMetrics {
  site_id?: number;
  url?: string;
  site_name?: string;
}

/**
 * 오버롤 레이팅 가중치 설정
 */
export const RATING_WEIGHTS = {
  fcp_mobile: 0.25,        // First Contentful Paint (모바일 중심)
  lcp_mobile: 0.25,        // Largest Contentful Paint (모바일 중심)
  tbt_mobile: 0.20,        // Total Blocking Time (반응성)
  total_size_mobile: 0.15, // 파일 크기 (효율성)
  fcp_desktop: 0.10,       // 데스크탑은 낮은 가중치
  lcp_desktop: 0.05        // 데스크탑은 낮은 가중치
};

// 모바일 우선 가중치 (2025.09 개선 - 팀 철학 반영)
export const MOBILE_WEIGHTS = {
  total_size_mobile: 0.40, // 파일크기 최우선 (기존 10% → 40%)
  tbt_mobile: 0.30,        // TBT 두 번째 (기존 25% → 30%)
  fcp_mobile: 0.15,        // FCP 비중 감소 (기존 35% → 15%)
  lcp_mobile: 0.15,        // LCP 비중 감소 (기존 30% → 15%)
  fcp_desktop: 0.00,       // 데스크탑 무시
  lcp_desktop: 0.00        // 데스크탑 무시
};

// 데스크탑 우선 가중치 (2025.09 개선 - 팀 철학 반영)
export const DESKTOP_WEIGHTS = {
  total_size_desktop: 0.40, // 파일크기 최우선 (기존 10% → 40%)
  tbt_desktop: 0.30,        // TBT 두 번째 (기존 25% → 30%)
  fcp_desktop: 0.15,        // FCP 비중 감소 (기존 35% → 15%)
  lcp_desktop: 0.15,        // LCP 비중 감소 (기존 30% → 15%)
  fcp_mobile: 0.00,        // 모바일 무시
  lcp_mobile: 0.00         // 모바일 무시
};

/**
 * 특정 지표에 대한 상대 순위 점수 계산 (0-1)
 * @param value 현재 사이트의 지표 값
 * @param allSites 전체 사이트 데이터
 * @param metric 측정할 지표명
 * @param direction 'lower_better' | 'higher_better'
 */
function getRankingScore(
  value: number | string | undefined,
  allSites: SiteData[],
  metric: keyof PerformanceMetrics,
  direction: 'lower_better' | 'higher_better' = 'lower_better'
): number {
  if (value === undefined || value === null) return 0;

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return 0;

  // 유효한 값들만 필터링
  const validValues = allSites
    .map(site => {
      const val = site[metric];
      if (val === undefined || val === null) return null;
      const parsed = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(parsed) ? null : parsed;
    })
    .filter((val): val is number => val !== null);

  if (validValues.length === 0) return 0;

  validValues.sort((a, b) => a - b);

  // 현재 값이 전체에서 몇 번째 순위인지 계산
  let worseCount = 0;
  for (const val of validValues) {
    if (direction === 'lower_better') {
      // 현재 값보다 나쁜(큰) 값들의 개수를 센다
      if (val > numValue) worseCount++;
    } else {
      // 현재 값보다 나쁜(작은) 값들의 개수를 센다
      if (val < numValue) worseCount++;
    }
  }

  // 상대 순위 점수 (0-1): 나보다 나쁜 사이트들의 비율
  return worseCount / validValues.length;
}

/**
 * 파일크기 점수 계산 (40MB 한계치 적용)
 * @param value 파일 크기 (bytes)
 * @param allSites 전체 사이트 데이터
 * @param metric 측정할 지표명
 * @param limitMB 한계치 (MB)
 */
function calculateSizeScoreWithLimit(
  value: number | string | undefined,
  allSites: SiteData[],
  metric: keyof PerformanceMetrics,
  limitMB: number = 40
): number {
  if (value === undefined || value === null) return 0;

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return 0;

  const sizeMB = numValue / (1024 * 1024);
  const baseScore = getRankingScore(value, allSites, metric, 'lower_better');

  // 40MB 초과 시 패널티 적용
  if (sizeMB > limitMB) {
    const penalty = Math.min(0.4, (sizeMB - limitMB) / 100);
    return Math.max(0, baseScore - penalty);
  }

  return baseScore;
}

/**
 * 비선형 스케일링 함수 (V1.1 방식 - 85점 이상 더욱 타이트)
 * 상위권일수록 점수 올리기 어렵게 만들어 변별력 극대화
 * @param weightedScore 가중 평균 점수 (0-1)
 * @returns 최종 점수 (60-100)
 */
function applyNonLinearScaling(weightedScore: number): number {
  // 0.0 -> 60, 0.5 -> 75, 0.75 -> 82, 0.85 -> 85, 0.92 -> 88, 0.97 -> 92, 1.0 -> 100
  if (weightedScore < 0.5) {
    // 하위 50%: 60-75점 (15점 구간)
    return 60 + (weightedScore * 2 * 15);
  } else if (weightedScore < 0.75) {
    // 중위 25%: 75-82점 (7점 구간)
    const normalized = (weightedScore - 0.5) / 0.25;
    return 75 + (normalized * 7);
  } else if (weightedScore < 0.85) {
    // 상위 10%: 82-85점 (3점 구간)
    const normalized = (weightedScore - 0.75) / 0.1;
    return 82 + (normalized * 3);
  } else if (weightedScore < 0.92) {
    // 최상위 7%: 85-88점 (3점 구간)
    const normalized = (weightedScore - 0.85) / 0.07;
    return 85 + (normalized * 3);
  } else if (weightedScore < 0.97) {
    // 초최상위 5%: 88-92점 (4점 구간)
    const normalized = (weightedScore - 0.92) / 0.05;
    return 88 + (normalized * 4);
  } else {
    // 극소수 3%: 92-100점 (8점 구간)
    const normalized = (weightedScore - 0.97) / 0.03;
    return 92 + (normalized * 8);
  }
}

/**
 * 개별 사이트의 오버롤 레이팅 계산
 * @param site 현재 사이트 데이터
 * @param allSites 전체 사이트 데이터 (명예의전당)
 * @returns 0-100점 오버롤 레이팅
 */
export function calculateOverallRating(site: SiteData, allSites: SiteData[]): number {
  if (!site || allSites.length === 0) return 0;

  // 각 지표별 상대 순위 점수 계산
  const scores = {
    fcp_mobile: getRankingScore(site.fcp_mobile, allSites, 'fcp_mobile', 'lower_better'),
    lcp_mobile: getRankingScore(site.lcp_mobile, allSites, 'lcp_mobile', 'lower_better'),
    tbt_mobile: getRankingScore(site.tbt_mobile, allSites, 'tbt_mobile', 'lower_better'),
    total_size_mobile: getRankingScore(site.total_size_mobile, allSites, 'total_size_mobile', 'lower_better'),
    fcp_desktop: getRankingScore(site.fcp_desktop, allSites, 'fcp_desktop', 'lower_better'),
    lcp_desktop: getRankingScore(site.lcp_desktop, allSites, 'lcp_desktop', 'lower_better')
  };

  // 가중 평균 계산
  const weightedScore = Object.entries(scores).reduce((total, [metric, score]) => {
    const weight = RATING_WEIGHTS[metric as keyof typeof RATING_WEIGHTS] || 0;
    return total + (score * weight);
  }, 0);

  // 100점 만점으로 변환
  const finalScore = Math.round(weightedScore * 100);

  // 0-100 범위로 제한
  return Math.max(0, Math.min(100, finalScore));
}

/**
 * 오버롤 레이팅에 따른 등급 반환
 */
export function getRatingGrade(rating: number): string {
  if (rating >= 90) return 'A+';
  if (rating >= 80) return 'A';
  if (rating >= 70) return 'B+';
  if (rating >= 60) return 'B';
  if (rating >= 50) return 'C+';
  if (rating >= 40) return 'C';
  return 'D';
}

/**
 * 오버롤 레이팅 색상 반환 (UI용)
 */
export function getRatingColor(rating: number): string {
  if (rating >= 90) return '#00C851'; // 초록
  if (rating >= 80) return '#39C0ED'; // 파랑
  if (rating >= 70) return '#FFB74D'; // 주황
  if (rating >= 60) return '#FF8A65'; // 연한 빨강
  if (rating >= 50) return '#FF7043'; // 빨강
  if (rating >= 40) return '#F44336'; // 진한 빨강
  return '#9E9E9E'; // 회색
}

/**
 * 명예의전당 API 응답에 오버롤 레이팅 추가
 */
export function addOverallRatingToSites(sites: SiteData[]): (SiteData & { overall_rating: number; rating_grade: string })[] {
  return sites.map(site => ({
    ...site,
    overall_rating: calculateOverallRating(site, sites),
    rating_grade: getRatingGrade(calculateOverallRating(site, sites))
  }));
}

/**
 * 모바일 우선 오버롤 레이팅 계산 (2025.09 개선)
 */
export function calculateMobileRating(site: SiteData, allSites: SiteData[]): number {
  if (!site || allSites.length === 0) return 0;

  // 각 지표별 상대 순위 점수 계산 (모바일 중심)
  const scores: Record<string, number> = {
    fcp_mobile: getRankingScore(site.fcp_mobile, allSites, 'fcp_mobile', 'lower_better'),
    lcp_mobile: getRankingScore(site.lcp_mobile, allSites, 'lcp_mobile', 'lower_better'),
    tbt_mobile: getRankingScore(site.tbt_mobile, allSites, 'tbt_mobile', 'lower_better'),
    total_size_mobile: calculateSizeScoreWithLimit(site.total_size_mobile, allSites, 'total_size_mobile', 40)
  };

  // 모바일 가중 평균 계산
  const weightedScore = Object.entries(scores).reduce((total, [metric, score]) => {
    const weight = MOBILE_WEIGHTS[metric as keyof typeof MOBILE_WEIGHTS] || 0;
    return total + (score * weight);
  }, 0);

  // 비선형 스케일링 적용 (60-100점 구간, 상위권 압축)
  const finalScore = applyNonLinearScaling(weightedScore);
  return Math.max(60, Math.min(100, Math.round(finalScore)));
}

/**
 * 데스크탑 우선 오버롤 레이팅 계산 (2025.09 개선)
 */
export function calculateDesktopRating(site: SiteData, allSites: SiteData[]): number {
  if (!site || allSites.length === 0) return 0;

  // 각 지표별 상대 순위 점수 계산 (데스크탑 중심)
  const scores: Record<string, number> = {
    fcp_desktop: getRankingScore(site.fcp_desktop, allSites, 'fcp_desktop', 'lower_better'),
    lcp_desktop: getRankingScore(site.lcp_desktop, allSites, 'lcp_desktop', 'lower_better'),
    tbt_desktop: getRankingScore(site.tbt_desktop, allSites, 'tbt_desktop', 'lower_better'),
    total_size_desktop: calculateSizeScoreWithLimit(site.total_size_desktop, allSites, 'total_size_desktop', 40)
  };

  // 데스크탑 가중 평균 계산
  const weightedScore = Object.entries(scores).reduce((total, [metric, score]) => {
    const weight = DESKTOP_WEIGHTS[metric as keyof typeof DESKTOP_WEIGHTS] || 0;
    return total + (score * weight);
  }, 0);

  // 비선형 스케일링 적용 (60-100점 구간, 상위권 압축)
  const finalScore = applyNonLinearScaling(weightedScore);
  return Math.max(60, Math.min(100, Math.round(finalScore)));
}

/**
 * 가중치 조정 함수 (향후 관리자 패널용)
 */
export function updateRatingWeights(newWeights: Partial<typeof RATING_WEIGHTS>): void {
  Object.assign(RATING_WEIGHTS, newWeights);

  // 가중치 합이 1이 되도록 정규화
  const totalWeight = Object.values(RATING_WEIGHTS).reduce((sum, weight) => sum + weight, 0);
  if (totalWeight !== 1) {
    Object.keys(RATING_WEIGHTS).forEach(key => {
      (RATING_WEIGHTS as any)[key] = (RATING_WEIGHTS as any)[key] / totalWeight;
    });
  }
}